import { z } from 'zod';
import { ClientBookMode } from '../enums.js';
import { IsoDateTime, NonEmptyName, Slug, Uuid } from '../primitives.js';

/** IANA timezone, e.g. "Europe/London". UK-first, but stored explicitly per salon. */
export const Timezone = z.string().min(1).max(64).default('Europe/London');

export const Salon = z
  .object({
    id: Uuid,
    name: NonEmptyName,
    slug: Slug.describe('public queue/booking slug, e.g. salonomy.com/q/<slug>'),
    timezone: Timezone,
    currency: z.literal('GBP'),
    clientBookMode: ClientBookMode,
    vatRegistered: z.boolean(),
    vatNumber: z.string().max(20).nullable(),
    // WS8 — public online-booking config (book.salonomy.com/<slug>). Optional on the contract so
    // older clients / fixtures that predate WS8 still satisfy the shape; the API always populates
    // them (defaults: disabled, 120-min lead, 60-day window).
    onlineBookingEnabled: z
      .boolean()
      .optional()
      .describe('master opt-in for client self-serve booking'),
    onlineBookingLeadMinutes: z
      .int()
      .min(0)
      .optional()
      .describe('earliest a public client may book, in minutes from now'),
    onlineBookingWindowDays: z
      .int()
      .min(1)
      .max(365)
      .optional()
      .describe('how far ahead a public client may book, in days'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Salon' });
export type Salon = z.infer<typeof Salon>;

export const CreateSalon = z
  .object({
    name: NonEmptyName,
    slug: Slug,
    timezone: Timezone.optional(),
    clientBookMode: ClientBookMode.default('shared'),
    vatRegistered: z.boolean().default(false),
    vatNumber: z.string().max(20).nullish(),
    onlineBookingEnabled: z.boolean().optional(),
    onlineBookingLeadMinutes: z.int().min(0).optional(),
    onlineBookingWindowDays: z.int().min(1).max(365).optional(),
  })
  .meta({ id: 'CreateSalon' });
export type CreateSalon = z.infer<typeof CreateSalon>;

export const UpdateSalon = CreateSalon.partial().meta({ id: 'UpdateSalon' });
export type UpdateSalon = z.infer<typeof UpdateSalon>;

export const Location = z
  .object({
    id: Uuid,
    salonId: Uuid,
    name: NonEmptyName,
    addressLine1: z.string().max(200).nullable(),
    addressLine2: z.string().max(200).nullable(),
    city: z.string().max(120).nullable(),
    postcode: z.string().max(12).nullable(),
    timezone: Timezone,
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Location' });
export type Location = z.infer<typeof Location>;

export const CreateLocation = Location.pick({
  name: true,
})
  .extend({
    addressLine1: z.string().max(200).nullish(),
    addressLine2: z.string().max(200).nullish(),
    city: z.string().max(120).nullish(),
    postcode: z.string().max(12).nullish(),
    timezone: Timezone.optional(),
  })
  .meta({ id: 'CreateLocation' });
export type CreateLocation = z.infer<typeof CreateLocation>;
